/**
 * Client-side mirror of `src/lib/rbac/permissions.ts`.
 *
 * The Next.js original reads `PagePermission` straight from Prisma inside the
 * request. A browser cannot do that, so the shape splits in two:
 *
 *   - the TYPES and the DECISION RULE are copied verbatim (below), because
 *     they are what SAFE-04 protects: a missing row is a DENY for role USER,
 *     an ADMIN short-circuits before any lookup, and `scope` is the
 *     CAPABILITY, not the scope actually applied (that is `effectiveScope`);
 *   - the DATA is fetched once per session and cached in a React context
 *     (`PermissionsProvider`), replacing the per-request Prisma read.
 *
 * The SPA gate is a CONVENIENCE, exactly as `middleware.ts` is. Neither is the
 * enforcement point: every API route re-resolves the permission server-side
 * (`requirePagePermission` on Next.js, `resolve_permission` in
 * `backend/api/app/rbac.py`). A tampered client can reach a route's markup and
 * will still get a 403 with no data — the same outcome as tampering with the
 * middleware today.
 */
import { PAGE_REGISTRY, type PageKey, type PageAction } from "./pages";

export interface PagePermissionRow {
  canView: boolean;
  viewScope: "OWN" | "ALL";
  canEdit: boolean;
  editScope: "OWN" | "ALL";
  canDelete: boolean;
  deleteScope: "OWN" | "ALL";
}

export interface PagePermissionResult {
  allowed: boolean;
  scope: "own" | "all";
}

export type Role = "ADMIN" | "USER";

const FULL_ROW: PagePermissionRow = {
  canView: true, viewScope: "ALL",
  canEdit: true, editScope: "ALL",
  canDelete: true, deleteScope: "ALL",
};

export const ADMIN_FULL_GRANT: Record<PageKey, PagePermissionRow> =
  Object.fromEntries(PAGE_REGISTRY.map((p) => [p.key, FULL_ROW])) as Record<PageKey, PagePermissionRow>;

export const DENY: PagePermissionResult = { allowed: false, scope: "own" };

/**
 * Whether a user may perform `action` on `pageKey`, and at what scope.
 *
 * Line-for-line the same decision as the server function of the same name:
 * ADMIN short-circuits before consulting the map (which is why an admin needs
 * no permissions fetch at all), and for a USER a MISSING ROW IS A DENY.
 */
export function resolvePermission(
  role: Role,
  permissions: Record<string, PagePermissionRow> | null,
  pageKey: PageKey,
  action: PageAction,
): PagePermissionResult {
  if (role === "ADMIN") return { allowed: true, scope: "all" };
  if (!permissions) return DENY;

  const row = permissions[pageKey];
  if (!row) return DENY;

  const fields = {
    view:   { allowed: row.canView,   scope: row.viewScope },
    edit:   { allowed: row.canEdit,   scope: row.editScope },
    delete: { allowed: row.canDelete, scope: row.deleteScope },
  } as const;
  return {
    allowed: fields[action].allowed,
    scope: fields[action].scope.toLowerCase() as "own" | "all",
  };
}

/**
 * The nav's visible set — the SPA's copy of the computation in
 * `src/app/(dashboard)/layout.tsx`:
 *
 *     PAGE_REGISTRY.filter((p) => role === "ADMIN" || perms[p.key]?.canView)
 *
 * Same filter, same order, same `PAGE_REGISTRY`. `TopBar` then intersects it
 * with `NAV_PAGES`, so the bar still draws at most the fixed seven.
 */
export function visiblePageKeys(
  role: Role,
  permissions: Record<string, PagePermissionRow> | null,
): PageKey[] {
  return PAGE_REGISTRY
    .filter((p) => role === "ADMIN" || Boolean(permissions?.[p.key]?.canView))
    .map((p) => p.key);
}

/** Wire shape of the permissions endpoint — one row per granted page. */
export interface PermissionWireRow extends PagePermissionRow {
  pageKey: string;
}

/**
 * Normalise the endpoint's payload into the map `resolvePermission` expects.
 *
 * Unknown page keys are DROPPED rather than trusted: a row naming a page this
 * build does not know about cannot grant anything here, and silently ignoring
 * it is safer than letting it through to a gate that would not recognise it.
 * Malformed rows are dropped for the same reason — the resulting missing entry
 * is a deny, which is the fail-closed direction.
 */
export function normalizePermissionRows(raw: unknown): Record<string, PagePermissionRow> {
  const known = new Set<string>(PAGE_REGISTRY.map((p) => p.key));
  const rows: unknown[] = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { rows?: unknown })?.rows)
      ? ((raw as { rows: unknown[] }).rows)
      : [];

  const out: Record<string, PagePermissionRow> = {};
  for (const entry of rows) {
    if (typeof entry !== "object" || entry === null) continue;
    const r = entry as Record<string, unknown>;
    const key = r.pageKey;
    if (typeof key !== "string" || !known.has(key)) continue;
    if (
      typeof r.canView !== "boolean" ||
      typeof r.canEdit !== "boolean" ||
      typeof r.canDelete !== "boolean"
    ) {
      continue;
    }
    const scope = (v: unknown): "OWN" | "ALL" => (v === "ALL" ? "ALL" : "OWN");
    out[key] = {
      canView: r.canView,
      viewScope: scope(r.viewScope),
      canEdit: r.canEdit,
      editScope: scope(r.editScope),
      canDelete: r.canDelete,
      deleteScope: scope(r.deleteScope),
    };
  }
  return out;
}
